import { getAppStore } from '../app/AppStore';
import { SessionServerSelectors, SessionSiteSelectors } from '../app/selectors/SessionInfoSelectors';
import { ServiceLocator } from '../app/ServiceLocator';
import { openReactConfirmationDialog } from '../commonInfra/dialogs/ReactConfirmationDialog';
import { sendSiteExpirationTelemetry, SiteExpirationActions } from './SiteExpirationTelemetry';
import { WindowLocationService } from '../commonInfra/services/WindowLocationService';
import { Messages } from '../codegen/Localize';
import { L10n } from '../ts/L10n/L10n';
import { LicensesService } from '../ts/services/LicensesService';
import { ToasterService, ToastType } from '../ts/services/ToasterService';
import { ILicenseConsumptionInfo } from '../ts/ServerApi/types/ILicenseConsumptionInfo';
import { TabDialogWidth } from '../commonInfra/dialogs/style/TabDialogWidth';

const webstoreUrl = '{baseUrl}/subscribe-now?site_id={siteUUID}&source=tol&tableau_creator={creator}&tableau_explorer={explorer}&tableau_viewer={viewer}';

export interface SiteExpirationParams  {
  site_uuid: string;
  creators: number;
  explorers: number;
  viewers: number;
}

type RedirectParams = {
  redirectUrl: string;
  siteExpirationParams: SiteExpirationParams;
}

export class SiteExpirationNotification {
  private notificationsShown: boolean;

  constructor() {
    this.notificationsShown = false;
  }

  public showNotificationsIfEnabled() {
    const state = getAppStore().getState();
    const isSiteExpiring: boolean = SessionSiteSelectors.isSiteExpiring(state);
    const siteTypeName: string = SessionSiteSelectors.siteTypeName(state);
    // We will display the masthead for a trial site no matter when it will expire 
    if (this.notificationsShown || !isSiteExpiring && siteTypeName !== 'Trial') {
      return;
    }

    // Fetch the capacity information from License Service
    const licenseService = ServiceLocator.get<LicensesService>('LicensesService');
    licenseService.hasSiteLicensingConsumptionInfo().then((hasInfo: boolean) => {
      if (hasInfo) {
        licenseService.fetchSiteScopedCapacityInfoForInfoBar().then((licenseInfo: ILicenseConsumptionInfo) => {
          this.showNotification(licenseInfo);
        });
      }
      else {
        licenseService.fetchCapacityInfoForInfoBar().then((licenseInfo: ILicenseConsumptionInfo) => {
          this.showNotification(licenseInfo);
        });
      }
    });
    this.notificationsShown = true;
  }

  private showNotification(licenseInfo: ILicenseConsumptionInfo) {
    const state = getAppStore().getState();
    const siteTypeName: string = SessionSiteSelectors.siteTypeName(state);
    const siteExpiringDays = SessionSiteSelectors.siteExpiringDays(state);
    let message: string;
    if (siteExpiringDays > 0) {
      if (siteTypeName === 'Trial') {
        message = Messages.siteDaysLeftInTrial({ SITE_EXPIRATION_DAYS: L10n.localizedCount(siteExpiringDays)});
      } else {
        message = Messages.siteWillExpireInDays({ SITE_EXPIRATION_DAYS: L10n.localizedCount(siteExpiringDays)});
      }
    }
    else {
      message = Messages.siteIsExpired();
    }
    let siteExpirationParams = {
      site_uuid: SessionSiteSelectors.siteUuid(state),
      creators: 0,
      explorers: 0,
      viewers: 0
    };

    if (licenseInfo) {
      siteExpirationParams.creators = licenseInfo.usedCreatorUsers;
      siteExpirationParams.explorers = licenseInfo.usedExplorerUsers;
      siteExpirationParams.viewers = licenseInfo.usedViewerUsers;
    }

    if (siteTypeName === 'Trial') {
      const subscribeNowEnabled = SessionSiteSelectors.subscribeNowEnabled(state);
      // If the LD feature flag "SubscribeNow" is enabled, redirect trial users to Webstore
      if (subscribeNowEnabled) {
        const baseUrl = SessionServerSelectors.webstoreUrl(state);
        const redirectUrl = webstoreUrl.replace('{baseUrl}', baseUrl)
                                       .replace('{siteUUID}', siteExpirationParams.site_uuid)
                                       .replace('{creator}', siteExpirationParams.creators.toString())
                                       .replace('{explorer}', siteExpirationParams.explorers.toString())
                                       .replace('{viewer}', siteExpirationParams.viewers.toString());
        sendSiteExpirationTelemetry(SiteExpirationActions.SubscribeNowDisplay, siteExpirationParams);
        ToasterService.instance.pop({
          type: ToastType.Info,
          text: message,
          testId: 'trial-site-expire-to-webstore',
          action: () => this.openSubscribeNowDialog({ redirectUrl: redirectUrl, siteExpirationParams: siteExpirationParams }),
          actionMessage: Messages.siteBuyNowButton(),
          keepOnLocationChange: true,
        });
      }
      // If the LD feature flag "SubscribeNow" is not enabled, redirect trial users to Contact Tableau Page
      else {
        const redirectUrl = SessionServerSelectors.siteContactSaleUrl(state);
        sendSiteExpirationTelemetry(SiteExpirationActions.ContactSaleDisplay, siteExpirationParams);
        ToasterService.instance.pop({
          type: ToastType.Warning,
          text: message,
          testId: 'trial-site-expire-to-contact-us',
          action: () => this.redirectToContactSalePage({ redirectUrl: redirectUrl, siteExpirationParams: siteExpirationParams }),
          actionMessage: Messages.siteContactSaleButton(),
          keepOnLocationChange: true,
        });
      }
    }
    // Any non-trial site is redirected to Renew Page
    else {
      const redirectUrl = SessionServerSelectors.siteRenewUrl(state);
      sendSiteExpirationTelemetry(SiteExpirationActions.RenewDisplay, siteExpirationParams);
      ToasterService.instance.pop({
        type: ToastType.Warning,
        text: message,
        testId: 'non-trial-site-expire-to-renew',
        action: () => this.redirectToRenewPage({ redirectUrl: redirectUrl, siteExpirationParams: siteExpirationParams }),
        actionMessage: Messages.siteRenewButton(),
        keepOnLocationChange: true,
      });
    }
  }

  // Redirect to Site Renew Page
  private redirectToRenewPage(params: RedirectParams) {
    sendSiteExpirationTelemetry(SiteExpirationActions.RenewClick, params.siteExpirationParams);
    WindowLocationService.open(params.redirectUrl, '_blank');
  }

  // Redirect to ContactSale Page
  private redirectToContactSalePage(params: RedirectParams) {
    sendSiteExpirationTelemetry(SiteExpirationActions.ContactSaleClick, params.siteExpirationParams);
    WindowLocationService.open(params.redirectUrl, '_blank');
  }

  // Open the SubscribeNow confirmation dialog
  private openSubscribeNowDialog(params: RedirectParams) {
    sendSiteExpirationTelemetry(SiteExpirationActions.SubscribeNowClick, params.siteExpirationParams);
    const initialModalInstance = openReactConfirmationDialog<RedirectParams, {}>({
      title: Messages.subscribeNow(),
      message: Messages.buyLicensesLeavingMessage(),
      actionName: Messages.buyLicensesWebstore(),
      action: (params: RedirectParams) => this.redirectToSubscribeNowPage(params),
      paramsForAction: (params),
      isActionDelete: false,
      dialogWidth: TabDialogWidth.width500px
    });
    return initialModalInstance.result;
  }

  // Redirect to SubscribeNow page (Webstore)
  private redirectToSubscribeNowPage(params: RedirectParams): Promise<any> {
    sendSiteExpirationTelemetry(SiteExpirationActions.SubscribeNowSubmit, params.siteExpirationParams);
    WindowLocationService.open(params.redirectUrl, '_blank');
    return;
  }
}


export const siteExpirationNotification = new SiteExpirationNotification();

